/**
 * The main bar: the big actions of the screen (see useMainBar). Two looks:
 *  - standard  equal tiles across the width, icon above the label (the old kiosk's bottom bar);
 *  - pills     centred rounded buttons, the primary one filled (the Essenziale theme).
 */
import { LANGUAGE_FLAGS } from '../../i18n/strings';
import { useTranslate } from '../../react/hooks';
import { Icon } from '../../ui-kit/Icon';
import { MenuRow } from './MenuRow';
import { useMainBar } from './useMainBar';
import css from './MainBar.module.css';
import pills from './Pills.module.css';

export function MainBarModule() {
  const bar = useMainBar();
  const t = useTranslate();
  if (bar.kind === 'none') return <div className={css.bar} />;
  if (bar.kind === 'menu') return <MenuRow bar={bar} look="tiles" />;
  if (bar.kind === 'languages') {
    return (
      <div className={css.bar} role="group" aria-label={t('language.choose')}>
        {bar.languages.map((lang) => (
          <button key={lang} className={css.tile} onClick={() => bar.choose(lang)}>
            <span className={css.flag} aria-hidden>{LANGUAGE_FLAGS[lang] ?? '🌐'}</span>
            <span>{t(`language.${lang}`)}</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className={css.bar}>
      {bar.actions.map((a) => (
        <button key={a.id} className={`${css.tile} ${a.tone ? css[a.tone] ?? '' : ''}`} onClick={() => bar.run(a)}>
          {a.icon && <Icon name={a.icon} size={36} className={css.icon} />}
          <span>{t(a.label)}</span>
        </button>
      ))}
    </div>
  );
}

/** The pills look. "Start over" lives in the minimal footer, so it is not repeated here. */
export function PillsBar() {
  const bar = useMainBar({ withRestart: false });
  const t = useTranslate();
  if (bar.kind === 'none') return <div className={pills.bar} />;
  if (bar.kind === 'menu') return <MenuRow bar={bar} look="pills" />;
  if (bar.kind === 'languages') {
    return (
      <div className={pills.bar} role="group" aria-label={t('language.choose')}>
        {bar.languages.map((lang) => (
          <button key={lang} className={pills.pill} onClick={() => bar.choose(lang)}>{LANGUAGE_FLAGS[lang] ?? ''} {t(`language.${lang}`)}</button>
        ))}
      </div>
    );
  }
  return (
    <div className={pills.bar}>
      {bar.actions.map((a) => (
        <button key={a.id} className={`${pills.pill} ${a.tone === 'primary' ? pills.primary : ''}`} onClick={() => bar.run(a)}>
          {t(a.label)}
        </button>
      ))}
    </div>
  );
}
